import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell } from "recharts";
import { getFindingsSeverityStatus } from "../../../utils/api/endpoints/dashboard/dashboardApi";
import { FiLoader } from "react-icons/fi";
import { MdInfo } from "react-icons/md";
import ChartFilter, { TimeRange, getPeriodLabel } from "./ChartFilter";

const STATUS_COLORS: Record<string, string> = {
  "Business Review": "#3B82F6", // Blue-500
  "Awaiting Fix": "#94A3B8", // Slate-400
  "Closed": "#22C55E", // Green-500
};

const SEVERITY_BG: Record<string, string> = {
  Critical: "bg-red-100 text-red-600",
  High: "bg-orange-100 text-orange-600",
  Medium: "bg-yellow-100 text-yellow-600",
  Low: "bg-green-100 text-green-600",
  Info: "bg-slate-100 text-slate-600",
};

// Custom Y-Axis tick to render severity boxes
const CustomYAxisTick = (props: any) => {
  const { x, y, payload } = props;
  const severity = payload.value;
  const colorClass = SEVERITY_BG[severity] || "bg-slate-100 text-slate-600";

  return (
    <g transform={`translate(${x},${y})`}>
      <foreignObject x={-100} y={-15} width={90} height={30}>
        <div className={`flex items-center justify-center w-full h-full rounded px-2 py-1 text-xs font-bold ${colorClass}`}>
          {severity}
        </div>
      </foreignObject>
    </g>
  );
};

interface FindingsBySeverityChartProps {
  isSubscriptionOnly?: boolean;
  selectedYear?: number | null;
  subscriptionStartDate?: string | null;
  enabled?: boolean;
}

// Module-level in-memory cache for instant tab-switching (0ms perceived latency)
interface FindingsSeverityChartCacheEntry {
  chartData: any[];
  periodLabel: string;
  timestamp: number;
}
const findingsChartCache = new Map<string, FindingsSeverityChartCacheEntry>();
const CACHE_FRESH_MS = 30_000;

function getChartCacheKey(
  timeFilter: string,
  subscriptionOnly?: boolean,
  selectedYear?: number | null
): string {
  return `${timeFilter}|${subscriptionOnly ?? false}|${selectedYear ?? ""}`;
}

const FindingsBySeverityChart: React.FC<FindingsBySeverityChartProps> = ({ 
  isSubscriptionOnly = false,
  selectedYear = null,
  subscriptionStartDate,
  enabled = true
}) => {
  const [timeFilter, setTimeFilter] = useState<TimeRange>(
    (selectedYear !== null || isSubscriptionOnly) ? 'yearly' : 'all'
  );

  const currentYear = new Date().getFullYear();
  const effectiveTimeFilter = (selectedYear && selectedYear !== currentYear) 
    ? 'yearly' 
    : timeFilter;

  const initialCacheKey = getChartCacheKey(
    effectiveTimeFilter,
    isSubscriptionOnly,
    selectedYear
  );
  const initialCached = findingsChartCache.get(initialCacheKey);

  const [chartData, setChartData] = useState<any[]>(initialCached ? initialCached.chartData : []);
  const [periodLabel, setPeriodLabel] = useState<string>(initialCached ? initialCached.periodLabel : 'Yearly');
  const [isLoading, setIsLoading] = useState(!initialCached);
  const [showInfo, setShowInfo] = useState(false);

  // Sync local timeFilter with global filters when they change
  useEffect(() => {
    if (selectedYear !== null || isSubscriptionOnly) {
      setTimeFilter('yearly');
    } else {
      setTimeFilter('all');
    }
  }, [selectedYear, isSubscriptionOnly]);

  useEffect(() => {
    if (!enabled) return;
    let isMounted = true;
    const cacheKey = getChartCacheKey(effectiveTimeFilter, isSubscriptionOnly, selectedYear);
    const entry = findingsChartCache.get(cacheKey);

    if (entry) {
      setChartData(entry.chartData);
      setPeriodLabel(entry.periodLabel);
      setIsLoading(false);
    } else {
      setIsLoading(true);
    }

    const fetchData = async () => {
      if (entry && Date.now() - entry.timestamp < CACHE_FRESH_MS) {
        return;
      }
      try {
        const response = await getFindingsSeverityStatus(effectiveTimeFilter, isSubscriptionOnly, selectedYear);
        if (isMounted && response?.success && response.data) {
          const rawData = response.data;
          const newPeriodLabel = rawData.periodInfo?.label || periodLabel;
          if (rawData.periodInfo?.label) {
            setPeriodLabel(rawData.periodInfo.label);
          }
          // Format data for Recharts
          const formattedData = [
            {
              name: 'Critical',
              'Business Review': rawData.critical?.BUSINESS_REVIEW || 0,
              'Awaiting Fix': rawData.critical?.AWAITING_FIX || 0,
              'Closed': rawData.critical?.CLOSED || 0,
            },
            {
              name: 'High',
              'Business Review': rawData.high?.BUSINESS_REVIEW || 0,
              'Awaiting Fix': rawData.high?.AWAITING_FIX || 0,
              'Closed': rawData.high?.CLOSED || 0,
            },
            {
              name: 'Medium',
              'Business Review': rawData.medium?.BUSINESS_REVIEW || 0,
              'Awaiting Fix': rawData.medium?.AWAITING_FIX || 0,
              'Closed': rawData.medium?.CLOSED || 0,
            },
            {
              name: 'Low',
              'Business Review': rawData.low?.BUSINESS_REVIEW || 0,
              'Awaiting Fix': rawData.low?.AWAITING_FIX || 0,
              'Closed': rawData.low?.CLOSED || 0,
            },
            {
              name: 'Info',
              'Business Review': rawData.info?.BUSINESS_REVIEW || 0,
              'Awaiting Fix': rawData.info?.AWAITING_FIX || 0,
              'Closed': rawData.info?.CLOSED || 0,
            }
          ];
          findingsChartCache.set(cacheKey, {
            chartData: formattedData,
            periodLabel: newPeriodLabel,
            timestamp: Date.now()
          });
          setChartData(formattedData);
        }
      } catch (e) {
        console.error("Failed to fetch findings by severity and status", e);
      } finally {
        if (isMounted) setIsLoading(false);
      }
    };

    fetchData();
    return () => { isMounted = false; };
  }, [effectiveTimeFilter, isSubscriptionOnly, selectedYear, enabled]);

  const displayFilterName = periodLabel;

  return (
    <motion.div
      className="bg-white rounded-xl shadow-sm border border-slate-100 flex flex-col h-full relative"
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2 }}
    >
      <div className="px-6 py-4 border-b border-gray-50 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex flex-col min-w-0 flex-1">
          <h3 className="text-sm font-semibold text-slate-800 break-words min-w-0">
            Findings by severity and status
          </h3>
          <span className="text-[11px] text-slate-400 font-semibold mt-0.5">
            {(selectedYear && selectedYear !== currentYear) ? `Year ${selectedYear}` : getPeriodLabel(timeFilter, isSubscriptionOnly, subscriptionStartDate)}
          </span>
        </div>
        
        <div className="flex items-center gap-2">
          <div className="relative flex-shrink-0">
            <MdInfo 
              className="h-4 w-4 text-slate-300 hover:text-blue-500 cursor-help transition-colors"
              onMouseEnter={() => setShowInfo(true)}
              onMouseLeave={() => setShowInfo(false)}
            />
            <AnimatePresence>
              {showInfo && (
                <motion.div
                  initial={{ opacity: 0, y: 10, scale: 0.95 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: 10, scale: 0.95 }}
                  className="absolute left-0 top-full mt-2 z-50 w-64 bg-[#010D2C] text-white p-3 rounded-xl text-[11px] font-medium shadow-xl pointer-events-none leading-relaxed"
                >
                  Shows the status of vulnerabilities across different severity levels, helping you identify where the current bottlenecks are (e.g., awaiting fix or in review).
                </motion.div>
              )}
            </AnimatePresence>
          </div>
          
          {(!selectedYear || selectedYear === currentYear) && (
            <ChartFilter
              active={timeFilter}
              onChange={setTimeFilter}
              isSubscriptionOnly={isSubscriptionOnly}
              subscriptionStartDate={subscriptionStartDate}
              supportsAllTime={!selectedYear}
              selectedYear={selectedYear}
            />
          )}
        </div>
      </div>

      <div className="pt-8 px-8 pb-3 flex-1 flex flex-col relative min-h-[390px]">
        <AnimatePresence>
          {isLoading && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 z-10 flex items-center justify-center bg-white/80 backdrop-blur-sm rounded-b-xl"
            >
              <FiLoader className="w-8 h-8 text-blue-500 animate-spin" />
            </motion.div>
          )}
        </AnimatePresence>

        <ResponsiveContainer width="100%" height="100%" debounce={50}>
          <BarChart
            data={chartData}
            layout="vertical"
            margin={{ top: 10, right: 30, left: 120, bottom: 40 }}
            barGap={2}
          >
            <CartesianGrid strokeDasharray="3 3" horizontal={false} vertical={true} stroke="#E2E8F0" />
            <XAxis 
              type="number" 
              allowDecimals={false}
              tick={{ fontSize: 11, fill: '#94A3B8', fontWeight: 600 }}
              axisLine={{ stroke: '#E2E8F0' }}
              tickLine={false}
              label={{ 
                value: 'Number of Findings', 
                position: 'insideBottom', 
                offset: -10, 
                style: { fill: '#6B7280', fontSize: 11, fontWeight: 700 } 
              }}
            />
            <YAxis 
              dataKey="name" 
              type="category" 
              tick={<CustomYAxisTick />} 
              axisLine={false}
              tickLine={false}
              label={{ 
                value: 'Severity Level', 
                angle: -90, 
                position: 'insideLeft', 
                offset: -95, 
                style: { fill: '#6B7280', fontSize: 11, fontWeight: 700, textAnchor: 'middle' } 
              }}
            />
            <Tooltip 
              cursor={{ fill: '#F8FAFC' }}
              contentStyle={{ 
                borderRadius: '12px', 
                border: 'none', 
                boxShadow: '0 10px 25px rgba(0,0,0,0.1)',
                fontSize: '12px',
                fontWeight: '600'
              }}
            />
            <Legend 
              verticalAlign="bottom" 
              align="center" 
              iconType="square" 
              wrapperStyle={{ fontSize: '11px', fontWeight: 'bold', color: '#64748B', bottom: '5px' }}
            />
            <Bar dataKey="Business Review" fill={STATUS_COLORS["Business Review"]} radius={[0, 4, 4, 0]} barSize={12} animationDuration={400} />
            <Bar dataKey="Awaiting Fix" fill={STATUS_COLORS["Awaiting Fix"]} radius={[0, 4, 4, 0]} barSize={12} animationDuration={400} />
            <Bar dataKey="Closed" fill={STATUS_COLORS["Closed"]} radius={[0, 4, 4, 0]} barSize={12} animationDuration={400} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </motion.div>
  );
};

export default React.memo(FindingsBySeverityChart);

